import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  FileText,
  Loader2,
  Receipt,
  Save,
  Send,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { confirmDialog, promptDialog } from '../lib/dialogs'
import { formatCents } from '../lib/money'
import InvoiceEditor from '../components/billing/InvoiceEditor'
import ImportTimeEntriesModal from '../components/billing/ImportTimeEntriesModal'
import EmitInvoiceModal from '../components/billing/EmitInvoiceModal'
import DeleteDraftModal, { type DeleteDraftChoice } from '../components/billing/DeleteDraftModal'
import RegisterPaymentModal from '../components/billing/RegisterPaymentModal'
import {
  type InvoiceDetail,
  type InvoiceLineInput,
  type InvoiceSeriesDto,
  type FundsProvisionDto,
  type FundsProvisionListResponse,
} from '@jr2os/shared'

export default function InvoiceDetailPage() {
  const { t } = useTranslation('billing')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [inv, setInv] = useState<InvoiceDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [provisions, setProvisions] = useState<FundsProvisionDto[]>([])
  const [lines, setLines] = useState<InvoiceLineInput[]>([])
  const [irpfRate, setIrpfRate] = useState('0')
  const [notes, setNotes] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [dueDate, setDueDate] = useState<string>('')
  const [fundsApplications, setFundsApplications] = useState<
    Array<{ provisionId: string; amountCents: number }>
  >([])
  const [showPayment, setShowPayment] = useState(false)
  const [showEmit, setShowEmit] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showImportTime, setShowImportTime] = useState(false)

  const reload = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const { invoice } = await api.get<{ invoice: InvoiceDetail }>(`/invoices/${id}`)
      setInv(invoice)
      setLines(
        invoice.lines.map((l) => ({
          position: l.position,
          productId: l.productId,
          description: l.description,
          quantity: l.quantity,
          unitPriceCents: l.unitPriceCents,
          costCents: l.costCents ?? 0,
          discountPct: l.discountPct,
          vatRate: l.vatRate,
          isSupplied: l.isSupplied,
        }))
      )
      setIrpfRate(invoice.irpfRate)
      setNotes(invoice.notes ?? '')
      setInternalNotes(invoice.internalNotes ?? '')
      setDueDate(invoice.dueDate?.slice(0, 10) ?? '')
      setFundsApplications(
        invoice.appliedProvisions.map((a) => ({
          provisionId: a.provisionId,
          amountCents: a.amountCents,
        }))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('detail.loadError'))
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    api.get<{ items: InvoiceSeriesDto[] }>('/invoice-series').then((r) => setSeries(r.items))
  }, [])

  useEffect(() => {
    if (!inv) return
    const params = new URLSearchParams({ clientId: inv.clientId, status: 'ACTIVE' })
    api
      .get<FundsProvisionListResponse>(`/funds-provisions?${params.toString()}`)
      .then((r) => setProvisions(r.items))
      .catch(() => setProvisions([]))
  }, [inv?.clientId])

  const isDraft = inv?.status === 'BORRADOR'
  const totalFundsApplied = fundsApplications.reduce((a, b) => a + b.amountCents, 0)

  const onSaveDraft = async () => {
    if (!inv) return
    setSaving(true)
    setError(null)
    try {
      const body = {
        lines,
        irpfRate,
        notes: notes || null,
        internalNotes: internalNotes || null,
        dueDate: dueDate || null,
        fundsApplications,
      }
      await api.patch(`/invoices/${inv.id}`, body)
      await reload()
    } catch (err) {
      setError(extractErr(err, t))
    } finally {
      setSaving(false)
    }
  }

  // La confirmación vive ahora en EmitInvoiceModal, que además deja elegir la
  // FECHA de emisión: facturar en agosto un trabajo de julio ya no obliga a
  // fechar la factura en agosto (y mandarla al trimestre que no toca).
  const onEmit = async (issueDate: string) => {
    if (!inv) return
    setSaving(true)
    setError(null)
    try {
      // Persist last changes before emitting
      await api.patch(`/invoices/${inv.id}`, {
        lines,
        irpfRate,
        notes: notes || null,
        internalNotes: internalNotes || null,
        dueDate: dueDate || null,
        fundsApplications,
      })
      await api.post(`/invoices/${inv.id}/emit`, { issueDate })
      setShowEmit(false)
      await reload()
    } catch (err) {
      setError(extractErr(err, t))
    } finally {
      setSaving(false)
    }
  }

  // Un borrador que viene del plan de facturación (una cuota o un hito vencido)
  // no se descarta borrándolo: el job de las 03:00 lo regenera. Ese caso pregunta
  // qué hacer con el origen; el borrador suelto sigue con su confirmación de
  // siempre.
  const onDelete = async () => {
    if (!inv) return
    if (inv.origin) {
      setShowDelete(true)
      return
    }
    if (!(await confirmDialog({ message: t('detail.confirmDelete'), tone: 'danger' }))) return
    await deleteDraft()
  }

  const deleteDraft = async (then?: DeleteDraftChoice) => {
    if (!inv) return
    setSaving(true)
    try {
      await api.delete(`/invoices/${inv.id}${then ? `?then=${then}` : ''}`)
      navigate('/billing')
    } catch (err) {
      setError(extractErr(err, t))
      setShowDelete(false)
      setSaving(false)
    }
  }

  // Copia la factura a un borrador nuevo y salta a él. Pensado sobre todo para
  // rehacer una factura anulada por rectificativa sin teclear otra vez las
  // líneas (el hito de la oportunidad ya está consumido y no sirve de atajo).
  const onDuplicate = async () => {
    if (!inv) return
    try {
      const { invoice } = await api.post<{ invoice: InvoiceDetail }>(
        `/invoices/${inv.id}/duplicate`
      )
      navigate(`/billing/${invoice.id}`)
    } catch (err) {
      setError(extractErr(err, t))
    }
  }

  const onRectify = async () => {
    if (!inv) return
    const reason = await promptDialog({
      title: t('detail.rectify'),
      message: t('detail.rectifyPrompt'),
      defaultValue: t('detail.rectifyDefaultReason'),
    })
    if (!reason) return
    try {
      const { invoice } = await api.post<{ invoice: InvoiceDetail }>(
        `/invoices/${inv.id}/rectify`,
        { reason }
      )
      navigate(`/billing/${invoice.id}`)
    } catch (err) {
      setError(extractErr(err, t))
    }
  }

  if (loading || !inv) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> {t('detail.loading')}
      </div>
    )
  }

  const seriesName = series.find((s) => s.id === inv.seriesId)?.name ?? inv.seriesPrefix

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to="/billing" className="text-xs t-faint hover:underline inline-flex items-center gap-1">
            <ArrowLeft size={12} /> {t('detail.backToBilling')}
          </Link>
          <div className="flex items-baseline gap-3 mt-1 flex-wrap">
            <span className="font-mono text-sm text-jr2-700 dark:text-jr2-300">{inv.number ?? t('detail.draftPlaceholder')}</span>
            <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
              <Receipt size={18} className="text-jr2-700 dark:text-jr2-300" />
              {inv.clientDisplayName}
            </h1>
            <span className="text-xs px-1.5 py-0.5 rounded border border-base t-secondary">
              {t('enums:InvoiceStatus.' + inv.status)}
            </span>
          </div>
          <p className="text-xs t-muted mt-0.5">
            {t('detail.seriesLabel')} {seriesName}
            {inv.opportunityCode && ` · ${inv.opportunityCode}`}
            {inv.emittedAt && ` · ${t('detail.emittedAt')} ${new Date(inv.emittedAt).toLocaleDateString(currentLocaleTag())}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDraft ? (
            <>
              <button
                type="button"
                onClick={() => void onSaveDraft()}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {t('detail.save')}
              </button>
              <button
                type="button"
                onClick={() => setShowEmit(true)}
                disabled={saving || lines.length === 0}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Send size={14} /> {t('detail.emit')}
              </button>
              <button
                type="button"
                onClick={() => void onDelete()}
                className="w-9 h-9 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                title={t('detail.deleteDraft')}
              >
                <Trash2 size={14} />
              </button>
            </>
          ) : (
            <>
              <a
                href={`/api/invoices/${inv.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
              >
                <FileText size={14} /> {t('detail.pdf')}
              </a>
              {/* También en ANULADA: es justo el caso de rehacer una factura
                  después de anularla por rectificativa. */}
              <button
                type="button"
                onClick={() => void onDuplicate()}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
                title={t('detail.duplicateHint')}
              >
                <Copy size={14} /> {t('detail.duplicate')}
              </button>
              {inv.status !== 'ANULADA' && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowPayment(true)}
                    className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
                  >
                    <CheckCircle2 size={14} /> {t('detail.registerPayment')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void onRectify()}
                    className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
                  >
                    {t('detail.rectify')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
        <div className="space-y-4">
          {/* Editor de líneas */}
          <div className="surface p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="t-primary font-semibold">{t('detail.lines')}</h3>
              {isDraft && (
                <button
                  type="button"
                  onClick={() => setShowImportTime(true)}
                  className="px-2.5 py-1 rounded-lg text-xs t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
                >
                  {t('detail.importTimes')}
                </button>
              )}
            </div>
            {isDraft ? (
              <InvoiceEditor
                value={lines}
                irpfRate={irpfRate}
                fundsAppliedCents={totalFundsApplied}
                onChange={setLines}
                clientId={inv.clientId}
              />
            ) : (
              <ReadonlyLines inv={inv} />
            )}
          </div>

          {/* Notas */}
          <div className="surface p-5 space-y-3">
            <div>
              <label className="block text-xs t-muted mb-1">{t('detail.notesVisible')}</label>
              <textarea
                disabled={!isDraft}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-xs t-muted mb-1">{t('detail.notesInternal')}</label>
              <textarea
                disabled={!isDraft}
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={2}
                className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary disabled:opacity-60"
              />
            </div>
          </div>

          {/* Pagos */}
          {inv.payments.length > 0 && (
            <div className="surface p-5">
              <h3 className="t-primary font-semibold mb-3">{t('detail.payments')}</h3>
              <ul className="divide-y divide-base/40 text-sm">
                {inv.payments.map((p) => (
                  <li key={p.id} className="py-2 flex items-center gap-3">
                    <span className="t-muted">{new Date(p.paidAt).toLocaleDateString(currentLocaleTag())}</span>
                    <span className="t-secondary text-xs">
                      {p.method ? t('enums:PaymentMethod.' + p.method) : '—'}
                    </span>
                    {p.reference && (
                      <span className="text-xs t-faint font-mono">{p.reference}</span>
                    )}
                    <span className="ml-auto font-mono t-primary">{formatCents(p.amountCents)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          {/* Margen interno (E6) — NUNCA visible para el cliente ni en el PDF */}
          <MarginSummary
            totalCostCents={inv.totalCostCents}
            marginCents={inv.marginCents}
            marginPct={inv.marginPct}
          />

          {isDraft && (
            <div className="surface p-4 text-sm">
              <h3 className="t-primary font-semibold mb-2">{t('detail.parameters')}</h3>
              <div className="space-y-2">
                <label className="block">
                  <span className="text-xs t-muted">{t('detail.irpf')}</span>
                  <select
                    value={irpfRate}
                    onChange={(e) => setIrpfRate(e.target.value)}
                    className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary mt-0.5"
                  >
                    <option value="0">0%</option>
                    <option value="0.07">7%</option>
                    <option value="0.15">15%</option>
                    <option value="0.19">19%</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs t-muted">{t('detail.dueDate')}</span>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary mt-0.5"
                  />
                </label>
                {/* Un borrador NO tiene fecha de emisión: se fija al emitir. Sin
                    decirlo, el único campo de fecha visible es el vencimiento y
                    parece que la factura ya está fechada. */}
                <p className="text-xs t-muted leading-relaxed">
                  {t('detail.issueDatePending')}
                </p>
              </div>
            </div>
          )}

          {/* Provisiones disponibles */}
          {isDraft && provisions.length > 0 && (
            <div className="surface p-4 text-sm">
              <h3 className="t-primary font-semibold mb-2">{t('detail.provisionsAvailable')}</h3>
              <ul className="space-y-2">
                {provisions.map((p) => {
                  const current = fundsApplications.find((fa) => fa.provisionId === p.id)
                  const applied = current?.amountCents ?? 0
                  return (
                    <li key={p.id} className="border border-base rounded p-2">
                      <div className="flex justify-between text-xs t-faint">
                        <span>{new Date(p.receivedAt).toLocaleDateString(currentLocaleTag())}</span>
                        <span className="font-mono">{formatCents(p.availableCents)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          max={p.availableCents / 100}
                          step="0.01"
                          value={applied ? (applied / 100).toFixed(2) : ''}
                          onChange={(e) => {
                            const cents = Math.round(Number(e.target.value || '0') * 100)
                            setFundsApplications((prev) => {
                              const others = prev.filter((fa) => fa.provisionId !== p.id)
                              return cents > 0
                                ? [...others, { provisionId: p.id, amountCents: cents }]
                                : others
                            })
                          }}
                          placeholder="0,00"
                          className="flex-1 bg-transparent border border-base rounded px-2 py-1 text-sm t-primary"
                        />
                        <span className="text-xs t-faint">€</span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Cadena de huellas propia de JR2 OS: no son registros VERI*FACTU (WP-01C) */}
          {inv.currentHash && (
            <div className="surface p-4 text-xs space-y-1">
              <h3 className="t-primary font-semibold text-sm mb-2">{t('detail.verifactu')}</h3>
              <div>
                <span className="t-faint">{t('detail.csv')}</span>{' '}
                <span className="font-mono text-jr2-700 dark:text-jr2-300">{inv.csv}</span>
              </div>
              <div className="truncate">
                <span className="t-faint">{t('detail.hash')}</span>{' '}
                <span className="font-mono t-secondary" title={inv.currentHash}>
                  {inv.currentHash.slice(0, 16)}…
                </span>
              </div>
              {inv.previousHash && (
                <div className="truncate">
                  <span className="t-faint">{t('detail.prev')}</span>{' '}
                  <span className="font-mono t-faint" title={inv.previousHash}>
                    {inv.previousHash.slice(0, 16)}…
                  </span>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {showImportTime && (
        <ImportTimeEntriesModal
          invoiceId={inv.id}
          clientId={inv.clientId}
          onClose={() => setShowImportTime(false)}
          onImported={async () => {
            setShowImportTime(false)
            await reload()
          }}
        />
      )}

      {showPayment && (
        <RegisterPaymentModal
          invoiceId={inv.id}
          pendingCents={inv.totalCents - inv.paidCents}
          onClose={() => setShowPayment(false)}
          onSaved={async () => {
            setShowPayment(false)
            await reload()
          }}
        />
      )}

      <DeleteDraftModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onChoose={(choice) => void deleteDraft(choice)}
        origin={inv?.origin ?? null}
        busy={saving}
      />
      <EmitInvoiceModal
        open={showEmit}
        busy={saving}
        onClose={() => setShowEmit(false)}
        onConfirm={(issueDate) => void onEmit(issueDate)}
      />
    </div>
  )
}

/** Resumen de margen comercial interno (E6). Nunca se muestra al cliente. */
function MarginSummary({
  totalCostCents,
  marginCents,
  marginPct,
}: {
  totalCostCents: number
  marginCents: number
  marginPct: number
}) {
  const { t } = useTranslation('billing')
  return (
    <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.04] p-4 text-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-amber-700 dark:text-amber-300/90 font-semibold flex items-center gap-1.5">
          {t('detail.marginInternal')}
        </h3>
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-700 dark:text-amber-300/70">
          {t('detail.internalBadge')}
        </span>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
          <span>{t('detail.cost')}</span>
          <span className="font-mono">{formatCents(totalCostCents)}</span>
        </div>
        <div className="flex justify-between text-amber-800 dark:text-amber-100/90 font-medium">
          <span>{t('detail.margin')}</span>
          <span className="font-mono">
            {formatCents(marginCents)}
            <span className="text-amber-700 dark:text-amber-300/60 ml-1">({marginPct.toFixed(0)}%)</span>
          </span>
        </div>
      </div>
      <p className="text-[10px] text-amber-700 dark:text-amber-300/50 mt-2">
        {t('detail.marginHint')}
      </p>
    </div>
  )
}

function ReadonlyLines({ inv }: { inv: InvoiceDetail }) {
  const { t } = useTranslation('billing')
  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase tracking-wider t-faint border-b border-base">
        <tr>
          <th className="text-left py-1">{t('detail.col.description')}</th>
          <th className="text-right py-1 w-16">{t('detail.col.quantity')}</th>
          <th className="text-right py-1 w-24">{t('detail.col.price')}</th>
          <th className="text-right py-1 w-14">{t('detail.col.vat')}</th>
          <th className="text-right py-1 w-24">{t('detail.col.total')}</th>
        </tr>
      </thead>
      <tbody>
        {inv.lines.map((l) => (
          <tr key={l.id} className="border-b border-base/30">
            <td className="py-2 t-primary">{l.description}</td>
            <td className="py-2 text-right t-secondary">{l.quantity}</td>
            <td className="py-2 text-right t-secondary font-mono">
              {formatCents(l.unitPriceCents)}
            </td>
            <td className="py-2 text-right text-xs t-faint">
              {l.isSupplied ? t('detail.suppliedShort') : `${(Number(l.vatRate) * 100).toFixed(0)}%`}
            </td>
            <td className="py-2 text-right t-primary font-mono">{formatCents(l.totalCents)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="text-sm">
        <tr>
          <td colSpan={4} className="py-2 text-right t-secondary">{t('detail.taxBase')}</td>
          <td className="py-2 text-right font-mono t-primary">{formatCents(inv.subtotalCents)}</td>
        </tr>
        {inv.vatBreakdown.map((v) => (
          <tr key={v.rate}>
            <td colSpan={4} className="py-1 text-right t-secondary">
              {t('detail.vatOn', { rate: (v.rate * 100).toFixed(0), base: formatCents(v.baseCents) })}
            </td>
            <td className="py-1 text-right font-mono">{formatCents(v.vatCents)}</td>
          </tr>
        ))}
        {inv.irpfCents > 0 && (
          <tr>
            <td colSpan={4} className="py-1 text-right text-rose-700 dark:text-rose-300">
              {t('detail.irpfRow', { rate: (Number(inv.irpfRate) * 100).toFixed(2) })}
            </td>
            <td className="py-1 text-right font-mono text-rose-700 dark:text-rose-300">
              -{formatCents(inv.irpfCents)}
            </td>
          </tr>
        )}
        {inv.fundsAppliedCents > 0 && (
          <tr>
            <td colSpan={4} className="py-1 text-right text-emerald-700 dark:text-emerald-300">
              {t('detail.provisionApplied')}
            </td>
            <td className="py-1 text-right font-mono text-emerald-700 dark:text-emerald-300">
              -{formatCents(inv.fundsAppliedCents)}
            </td>
          </tr>
        )}
        <tr className="border-t border-base">
          <td colSpan={4} className="pt-2 text-right t-primary font-semibold text-base">
            {t('detail.total')}
          </td>
          <td className="pt-2 text-right font-mono t-primary font-semibold text-base">
            {formatCents(inv.totalCents)}
          </td>
        </tr>
      </tfoot>
    </table>
  )
}

function extractErr(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined
    return body?.error ?? err.message
  }
  return err instanceof Error ? err.message : t('detail.unexpectedError')
}
